import React, {
  useState, useMemo, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';

import { Search, Close } from '@picsio/icons';
import {
  Icon, InputAdornment, Input, IconButton,
} from '@picsio/ui';
import Logger from '../../../../services/Logger';
import Tooltip from '../../../Tooltip';
import l18n from '../../../../shared/strings';

const AUTOCOMPLETE_OFF = { autoComplete: 'off' };

function TranscriptSearch({
  placeholder = '', setSearchValue, disabled = false, assetId, tabName,
}) {
  const [open, setOpen] = useState(false);
  const [currentValue, setValue] = useState('');

  const inputRef = useRef(null);

  const debounceSearch = _debounce((action, searchValue) => {
    action(searchValue);
    Logger.log('User', 'VideoIntelligenceSearchPerformed', {
      assetId,
      tabName,
      search_context: searchValue,
    });
  }, 300);

  const handleShowSearch = useCallback(() => {
    setOpen(true);

    const inputTag = inputRef.current && inputRef.current.querySelector('input');
    if (inputTag) {
      inputTag.focus();
    }
  }, []);

  const handleSearch = (e) => {
    const { value } = e.target;
    setValue(value);
    if (setSearchValue) {
      debounceSearch(setSearchValue, value);
    }
  };

  const handleResetSearch = () => {
    setValue('');
    setSearchValue('');
    if (!currentValue) {
      setOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    const keyEsc = 27;
    if (e.keyCode === keyEsc) {
      handleResetSearch();

      if (!currentValue) {
        setOpen(false);
      }
    }
  };

  const handleFocus = useCallback(() => {
    Logger.log('User', 'VideoIntelligenceSearchFocused', {
      assetId,
      tabName,
    });
  }, [assetId, tabName]);

  const inputClassName = useMemo(
    () => (open ? 'TranscriptSearch__input' : 'TranscriptSearch__input TranscriptSearch__input--hidden'),
    [open],
  );

  return (
    <div className="TranscriptSearch">
      <Tooltip content={l18n.TRANSCRIPT.tooltipSearch} placement="top">
        <IconButton size="lg" onClick={handleShowSearch} disabled={disabled}>
          <Search />
        </IconButton>
      </Tooltip>
      <Input
        ref={inputRef}
        type="text"
        name="search"
        className={inputClassName}
        placeholder={placeholder || ''}
        value={currentValue}
        onKeyDown={handleKeyDown}
        onChange={handleSearch}
        onFocus={handleFocus}
        inputProps={AUTOCOMPLETE_OFF}
        endAdornment={(
          <InputAdornment className="TranscriptSearch__icon-close" position="end" onClick={handleResetSearch}>
            <Icon color="inherit" size="lg">
              <Close />
            </Icon>
          </InputAdornment>
        )}
        startAdornment={(
          <InputAdornment>
            <Icon color="inherit" size="lg">
              <Search />
            </Icon>
          </InputAdornment>
        )}
      />
    </div>
  );
}

TranscriptSearch.propTypes = {
  setSearchValue: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default TranscriptSearch;
